import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { DatePicker } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { useApplicationConfig } from '~/data/applicationConfig';
import { CostCenter } from '~/data/costCenters';
import { EmpBasicData, EmpPosition, EmploymentData } from '~/data/employee';
import { EmploymentDetailType } from '~/data/employment/useEmploymentDetails';
import { useAllLegalEntities } from '~/data/legalEntity';
import { useOrgTree } from '~/data/orgunits';
import { BusinessUnitComponent } from '~/features/RehireDialog/components/BusinessUnitsComponent';
import { CostCenterComponents } from '~/features/RehireDialog/components/CostCenterComponents';
import { EmploymentStatusComponent } from '~/features/RehireDialog/components/EmploymentStatusComponent';
import { EmploymentTypeComponent } from '~/features/RehireDialog/components/EmploymentTypeComponent';
import { InternalPositionComponent } from '~/features/RehireDialog/components/InternalPositionComponent';
import { JobGradeComponent } from '~/features/RehireDialog/components/JobGradeComponent/component';
import { PersonnelGroupComponents } from '~/features/RehireDialog/components/PersonnelGroupComponents';
import { UnitPositionComponent } from '~/features/RehireDialog/components/UnitPositionsComponent';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { LegacyMuiButton } from '~/legacy-components/LegacyForms';
import { convertToMomentOrNull } from '~/utils/helpers/date';

import { rehireEmployee } from './data';
import { resetEnumsFormValues } from './helpers';
import { Form, useRehireEmpForm } from './hooks/useRehireEmpForm';
import { useSxStyles } from './useStyles';

interface Props {
  empId: number;
  empBasicData: EmpBasicData;
  empPosition: EmpPosition;
  employmentDetails: EmploymentDetailType | undefined;
  employmentData: EmploymentData;
}

const dateFormat = 'YYYY-MM-DD';

export const RehireDialog = ({
  empId,
  empBasicData,
  empPosition,
  employmentDetails,
  employmentData,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sxStyles = useSxStyles();
  const { t } = useTranslation();
  const { legalEntities } = useAllLegalEntities();
  const { applicationConfig } = useApplicationConfig();
  const { orgTree } = useOrgTree();
  const {
    getValues,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    helperValues,
    allEnumsLoading,
  } = useRehireEmpForm({
    empId,
    empPosition,
    empBasicData,
    employmentDetails,
    employmentData,
  });
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const { enqueueSnackbar } = useSnackbar();

  const {
    initialFormValues,
    allEnums,
    costCenterValues,
    unitPositions,
    managers,
    allEmployees,
    currencies,
    trialPeriodOptions,
    terminationReasons,
    fixedTermReasons,
    restrictField,
    formTemplatesEnabled,
  } = helperValues;

  const { vacationCalculationDayOptions } = useMemo(() => {
    const is7DaysEnabled =
      applicationConfig?.fComConfig?.absConfig?.fComEnable7DaysVacationRule ??
      false;

    const vacationCalculationDayOptions = is7DaysEnabled
      ? ['5', '6', '7']
      : ['5', '6'];

    return {
      is7DaysEnabled,
      vacationCalculationDayOptions,
    };
  }, [applicationConfig]);

  const trialPeriod = trialPeriodOptions.find(
    (option) => option.code === employmentData?.fEmplTrialPeriod,
  );
  const earliestStartDate = moment(employmentData.fEmplLastDateEmployment).add(
    1,
    'day',
  );

  const requiredText = t('fieldIsRequired');

  const [
    endOfEmploymentField,
    fEmplTrialPeriod,
    fEmplDateStart,
    fEmpPersonNumber,
    fEmpMainContract,
  ] = useWatch({
    control,
    name: [
      'rehire.employment.fEmplLastDateEmployment',
      'rehire.employment.fEmplTrialPeriod',
      'rehire.employment.fEmplDateStart',
      'rehire.employment.fEmpPersonNumber',
      'rehire.employment.fEmpMainContract',
    ],
  });

  const dialogOnClose = () => {
    setOpen(false);
    history.push(location.pathname.slice(3));
    reset(initialFormValues);
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const hasFormErrors: boolean = Object.keys(errors).length > 0;

  const EmpPersonNumberChanged = () => (
    <Box component="span" sx={sxStyles.warningMessage}>
      {t('warning_empNumberChange')}
    </Box>
  );

  const onAfterSubmit = (success: boolean) => {
    if (success) {
      dialogOnClose();
      window.location.reload();
      enqueueSnackbar(t('rehireSuccess'), { variant: 'success' });
    } else {
      enqueueSnackbar(t('rehireError'), { variant: 'error' });
    }
    setSubmitting(false);
  };

  const onSave = () => {
    return handleSubmit((form) => {
      const data = { ...form.rehire };
      const fEmpCostCenter = data.empPosition.fEmpCostCenter as number;
      const fEmpAddlCostCenters = data.empPosition.fEmpAddlCostCenters;
      const fEmpBusinessUnit1 = data.empPosition.fEmpBusinessUnit1;
      const fEmplDateStart = data.employment.fEmplDateStart as string;

      if (moment(fEmplDateStart).isSameOrBefore(moment().format())) {
        data.empPosition.fInternalPositionIsPending = false;
        data.employmentDetails.fEmplDetailIsPending = false;
      } else {
        data.empPosition.fInternalPositionIsPending = true;
        data.employmentDetails.fEmplDetailIsPending = true;
      }
      data.empPosition.fEmpCostCenter = undefined;
      data.employmentDetails.fEmplDetailStartDate = fEmplDateStart;
      data.empPosition.fEmpCostCenters = [
        { fEmpCostCenter: +fEmpCostCenter, fEmpCostCenterIsDefault: true },
        ...(fEmpAddlCostCenters as CostCenter[]).map((cc) => ({
          fEmpCostCenter: cc.fCostCenterId,
        })),
      ];
      data.empPosition.fInternalPositionUnit = fEmpBusinessUnit1;
      data.empPosition.fInternalPositionStartDate = fEmplDateStart;
      data.empPosition.fEmpAddlCostCenters = (
        fEmpAddlCostCenters as CostCenter[]
      ).map((cc) => `${cc?.fCostCenterId}`) as string[];

      setSubmitting(true);
      rehireEmployee(empId, data, onAfterSubmit);
    });
  };

  if (allEnumsLoading) return null;

  return (
    <>
      <LegacyMuiButton
        onClick={() => {
          formTemplatesEnabled === 'yes'
            ? history.push(`/portal/employee/${empId}/rehire`)
            : handleOpenDialog();
        }}
        sx={sxStyles.btnLeftMargin}
        startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Edit} />}
      >
        {t('fRehireBtnLabel')}
      </LegacyMuiButton>
      <DraggableDialog
        open={open || searchParams.get('mode') === 'rehire'}
        onClose={dialogOnClose}
        maxWidth={'lg'}
        PaperProps={{ sx: sxStyles.dialogPaper }}
      >
        <SolaForceDialogTitle
          icon={<LegacyFAIcon FAIcon={LegacyFAIcons.Edit} />}
          onClose={dialogOnClose}
        >
          {t('laReHire')}
        </SolaForceDialogTitle>
        <Divider />
        <DialogContent>
          <form autoComplete="off">
            <Grid
              container
              columnGap={4}
              columnSpacing={2}
              justifyContent="center"
            >
              <Grid item xs={5}>
                <Controller
                  control={control}
                  name={'rehire.employment.fEmpContractNumber'}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value}
                      sx={{
                        ...sxStyles.selectStyle,
                        ...sxStyles.inputBottomMargin,
                      }}
                      onChange={onChange}
                      label={t('fEmpContractNumber')}
                      disabled={!restrictField.hr}
                    />
                  )}
                />
                <Grid item container>
                  <Controller
                    control={control}
                    name={'rehire.employment.fEmpMainContract'}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        label={t('fEmpMainContract')}
                        sx={sxStyles.inputBottomMargin}
                        control={
                          <Checkbox
                            disabled={fEmpMainContract || value === true}
                            checked={fEmpMainContract || false}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                        }
                      />
                    )}
                  />
                </Grid>
                <Controller
                  control={control}
                  name={'rehire.employment.fEmpPersonNumber'}
                  rules={{ required: requiredText }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      required
                      disabled={
                        restrictField.manager && !restrictField.inMyHRUnit
                      }
                      value={value}
                      sx={{
                        ...sxStyles.selectStyle,
                        ...sxStyles.inputBottomMargin,
                      }}
                      onChange={onChange}
                      label={t('fEmpPersonNumber')}
                      helperText={
                        fEmpPersonNumber !== empBasicData.fEmpPersonNumber && (
                          <EmpPersonNumberChanged />
                        )
                      }
                    />
                  )}
                />
                <Grid item container>
                  <Controller
                    control={control}
                    name={'rehire.empPosition.fEmpLegalEntity'}
                    rules={{ required: requiredText }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        disabled={restrictField.manager && !restrictField.hr}
                        sx={{
                          ...sxStyles.selectStyle,
                          ...sxStyles.inputBottomMargin,
                        }}
                        placeholder={' '}
                        options={legalEntities}
                        getOptionLabel={(c) =>
                          `${c.fLegalEntityName} (${c.fLegalEntityCode})`
                        }
                        isOptionEqualToValue={(o, v) =>
                          o.fLegalEntityId === v.fLegalEntityId
                        }
                        value={
                          legalEntities.find(
                            (entity) => `${entity.fLegalEntityId}` === value,
                          ) || null
                        }
                        onChange={(e, value) => {
                          const fLegalEntityId =
                            value?.fLegalEntityId.toString() || '';
                          onChange(fLegalEntityId);
                          resetEnumsFormValues(
                            fLegalEntityId,
                            +getValues(
                              'rehire.empPosition.fInternalPositionUnit',
                            ),
                            allEnums,
                            { getValues, setValue } as UseFormReturn<Form>,
                            legalEntities,
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            required
                            {...params}
                            label={t('fEmpLegalEntity')}
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <InternalPositionComponent
                  control={control}
                  allEnums={allEnums}
                />
                <Grid item container>
                  <Controller
                    control={control}
                    name={'rehire.empPosition.fEmpJobTitle'}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        value={value}
                        sx={{
                          ...sxStyles.selectStyle,
                          ...sxStyles.inputBottomMargin,
                        }}
                        onChange={onChange}
                        label={t('fEmpJobTitle')}
                      />
                    )}
                  />
                </Grid>
                <Grid item container>
                  <Controller
                    control={control}
                    rules={{ required: requiredText }}
                    name="rehire.empPosition.fEmpBusinessUnit1"
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        options={orgTree[0].units}
                        getOptionLabel={(o) => o.fTreeUnitName}
                        isOptionEqualToValue={(o, v) =>
                          o.fTreeUnitId === v.fTreeUnitId
                        }
                        onChange={(_, value) => onChange(value?.fTreeUnitId)}
                        value={
                          orgTree[0].units.find(
                            (o) => o.fTreeUnitId === value,
                          ) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={orgTree[0].fTreeName}
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                        sx={{
                          ...sxStyles.selectStyle,
                          ...sxStyles.inputBottomMargin,
                        }}
                      />
                    )}
                  />
                </Grid>
                <BusinessUnitComponent control={control} />
                <UnitPositionComponent
                  control={control}
                  unitPositions={unitPositions}
                  getValues={getValues}
                  empData={empPosition}
                />
                <CostCenterComponents
                  control={control}
                  getValues={getValues}
                  setValue={setValue}
                  costCenterValues={costCenterValues}
                />
                <Controller
                  control={control}
                  rules={{
                    required: requiredText,
                    validate: (value) => {
                      // Is valid employee Id
                      const num = parseInt(value.toString());
                      return !isNaN(num) && num > 0;
                    },
                  }}
                  name={'rehire.empPosition.fInternalPositionManager'}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Autocomplete
                      sx={{
                        ...sxStyles.selectStyle,
                        ...sxStyles.inputBottomMargin,
                      }}
                      placeholder={''}
                      options={managers || []}
                      getOptionLabel={(c) =>
                        `${allEmployees[c]?.fEmpLastName || ''}, ${
                          allEmployees[c]?.fEmpFirstName || ''
                        }`
                      }
                      onChange={(e, value) => {
                        onChange(value?.toString());
                      }}
                      value={
                        managers?.find((mg) => mg.toString() === value) || null
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label={t('laManager')}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={5}>
                <EmploymentTypeComponent
                  control={control}
                  allEnums={allEnums}
                />
                <Grid item container>
                  <EmploymentStatusComponent
                    control={control}
                    allEnums={allEnums}
                  />
                </Grid>
                <Grid item container>
                  <Controller
                    control={control}
                    name={'rehire.employment.fEmplCurrency'}
                    rules={{ required: requiredText }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        sx={{
                          ...sxStyles.selectStyle,
                          ...sxStyles.inputBottomMargin,
                        }}
                        placeholder={''}
                        options={currencies}
                        value={value}
                        onChange={(e, value) => {
                          onChange(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            label={t('fCurrency')}
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item container>
                  <Controller
                    name={'rehire.employment.fEmplDateStart'}
                    control={control}
                    rules={{ required: requiredText }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        value={convertToMomentOrNull(value)}
                        minDate={earliestStartDate}
                        onChange={(v: moment.Moment | null) => {
                          if (v) {
                            const empTrialPeriodValue = getValues(
                              'rehire.employment.fEmplTrialPeriod',
                            );
                            if (empTrialPeriodValue) {
                              setValue(
                                'rehire.employment.fEmplTrialPeriodEndDate',
                                moment(v)
                                  .add(
                                    parseInt(empTrialPeriodValue.charAt(1)),
                                    'months',
                                  )
                                  .subtract(1, 'days')
                                  .format(dateFormat),
                              );
                            } else {
                              setValue(
                                'rehire.employment.fEmplTrialPeriodEndDate',
                                null,
                              );
                            }
                            setValue(
                              'rehire.employmentDetails.fEmplDetailStartDate',
                              v.format(dateFormat),
                            );
                            setValue(
                              'rehire.empPosition.fInternalPositionStartDate',
                              v?.format(dateFormat),
                            );
                          }
                          setValue(
                            'rehire.employment.fEmplDateArrival',
                            v?.format(dateFormat) || null,
                          );
                          onChange(v?.format(dateFormat));
                        }}
                        label={t('fEmplDateStart')}
                        slotProps={{
                          textField: {
                            required: true,
                            error: !!error,
                            helperText: error?.message,
                            sx: {
                              ...sxStyles.dateInput,
                              ...sxStyles.inputBottomMargin,
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item container>
                  <Controller
                    name={'rehire.employment.fEmplDateArrival'}
                    control={control}
                    rules={{ required: requiredText }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        value={convertToMomentOrNull(value)}
                        minDate={earliestStartDate}
                        onChange={(v: moment.Moment | null) => {
                          onChange(v?.format(dateFormat));
                        }}
                        label={t('fArrivalDate')}
                        slotProps={{
                          textField: {
                            required: true,
                            error: !!error,
                            helperText: error?.message,
                            sx: {
                              ...sxStyles.dateInput,
                              ...sxStyles.inputBottomMargin,
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Controller
                  control={control}
                  name={'rehire.employment.fEmplTrialPeriod'}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      options={trialPeriodOptions}
                      getOptionLabel={(o) => o.name}
                      isOptionEqualToValue={(o, v) => o.code === v.code}
                      onChange={(_, value) => {
                        onChange(value?.code);
                        if (value?.code) {
                          setValue(
                            'rehire.employment.fEmplTrialPeriodEndDate',
                            moment(fEmplDateStart)
                              .add(parseInt(value?.code?.charAt(1)), 'months')
                              .subtract(1, 'days')
                              .format(dateFormat),
                          );
                        }
                      }}
                      defaultValue={trialPeriod}
                      value={
                        trialPeriodOptions.find((o) => o.code === value) || null
                      }
                      renderInput={(params) => (
                        <TextField {...params} label={t('fTrialPeriod')} />
                      )}
                      sx={{
                        ...sxStyles.selectStyle,
                        ...sxStyles.inputBottomMargin,
                      }}
                    />
                  )}
                />
                <Grid item container>
                  <Controller
                    name={'rehire.employment.fEmplTrialPeriodEndDate'}
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        label={t('laTrialPeriodEndDate')}
                        onChange={(v: moment.Moment | null) =>
                          onChange(v?.format(dateFormat))
                        }
                        value={convertToMomentOrNull(value)}
                        minDate={earliestStartDate}
                        slotProps={{
                          textField: {
                            required: !!fEmplTrialPeriod,
                            error: !!error,
                            helperText: error?.message,
                            sx: {
                              ...sxStyles.dateInput,
                              ...sxStyles.inputBottomMargin,
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item container>
                  <Controller
                    name={'rehire.employment.fEmplPlannedEndDate'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        label={t('fEmplPlannedEndDate')}
                        onChange={(v: moment.Moment | null) =>
                          onChange(v?.format(dateFormat))
                        }
                        value={convertToMomentOrNull(value)}
                        minDate={
                          moment(
                            getValues('rehire.employment.fEmplDateStart'),
                          ) || undefined
                        }
                        slotProps={{
                          textField: {
                            sx: {
                              ...sxStyles.dateInput,
                              ...sxStyles.inputBottomMargin,
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid
                  justifyContent="flex-start"
                  alignItems="center"
                  display="flex"
                  item
                  container
                >
                  <Controller
                    name={'rehire.employment.fEmplLastDateEmployment'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        label={t('fLastDEmpl')}
                        minDate={
                          moment(
                            getValues('rehire.employment.fEmplDateStart'),
                          ) ||
                          earliestStartDate ||
                          undefined
                        }
                        onChange={(v: moment.Moment | null) => {
                          onChange(v?.format(dateFormat));
                          setValue(
                            'rehire.employment.fEmplTerminationDate',
                            v?.format(dateFormat) || null,
                          );
                        }}
                        value={convertToMomentOrNull(value)}
                        slotProps={{
                          textField: {
                            sx: sxStyles.inputBottomMargin,
                          },
                        }}
                      />
                    )}
                  />
                  <Button
                    onClick={() => {
                      setValue(
                        'rehire.employment.fEmplLastDateEmployment',
                        null,
                        { shouldDirty: true },
                      );
                      setValue('rehire.employment.fEmplTerminationDate', null, {
                        shouldDirty: true,
                      });
                      setValue('rehire.employment.fEmplTerminationReason', '', {
                        shouldDirty: true,
                      });
                    }}
                    startIcon={<i className="fa fa-undo"></i>}
                    sx={sxStyles.clearBtn}
                  >
                    {t('laClear')}
                  </Button>
                </Grid>

                <Controller
                  name={'rehire.employment.fEmplTerminationDate'}
                  control={control}
                  rules={{ required: !!endOfEmploymentField }}
                  render={({ field: { onChange, value } }) => (
                    <DatePicker
                      disabled={restrictField.notAllowEmpTermDate}
                      label={t('fLastDTemp')}
                      onChange={(v: moment.Moment | null) =>
                        onChange(v?.format(dateFormat))
                      }
                      value={convertToMomentOrNull(value)}
                      minDate={
                        moment(getValues('rehire.employment.fEmplDateStart')) ||
                        undefined
                      }
                      maxDate={
                        endOfEmploymentField
                          ? moment(endOfEmploymentField)
                          : undefined
                      }
                      slotProps={{
                        textField: {
                          required: !!endOfEmploymentField,
                          sx: {
                            ...sxStyles.dateInput,
                            ...sxStyles.inputBottomMargin,
                          },
                        },
                      }}
                    />
                  )}
                />
                <Grid item container>
                  <Controller
                    control={control}
                    name={'rehire.employment.fEmplTerminationReason'}
                    rules={{
                      required: !!endOfEmploymentField && requiredText,
                    }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        options={terminationReasons}
                        getOptionLabel={(o) => o?.name || ''}
                        isOptionEqualToValue={(o, v) => o?.code === v?.code}
                        onChange={(_, value) => onChange(value?.code)}
                        value={
                          terminationReasons.find((o) => o?.code === value) ||
                          null
                        }
                        sx={{
                          ...sxStyles.selectStyle,
                          ...sxStyles.inputBottomMargin,
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required={!!endOfEmploymentField}
                            label={t('laTerminationReason')}
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item container>
                  <Controller
                    control={control}
                    name={'rehire.employment.fEmplFixedTermReason'}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        options={fixedTermReasons}
                        getOptionLabel={(o) => o?.name || ''}
                        isOptionEqualToValue={(o, v) => o?.code === v?.code}
                        onChange={(_, value) => onChange(value?.code)}
                        value={
                          fixedTermReasons.find((o) => o?.code === value) ||
                          null
                        }
                        sx={{
                          ...sxStyles.selectStyle,
                          ...sxStyles.inputBottomMargin,
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label={t('fReasonFixTerm')} />
                        )}
                      />
                    )}
                  />
                </Grid>
                <JobGradeComponent control={control} allEnums={allEnums} />
                <Controller
                  control={control}
                  name={'rehire.employmentDetails.fEmplDetailTaxNumber'}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value}
                      sx={{
                        ...sxStyles.selectStyle,
                        ...sxStyles.inputBottomMargin,
                      }}
                      onChange={onChange}
                      label={t('fEmplDetailTaxNumber')}
                    />
                  )}
                />
                <Controller
                  name={'rehire.employmentDetails.fEmplDetailVacBalCalcSixDays'}
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <FormControl sx={sxStyles.inputBottomMargin}>
                        <FormLabel id="vacation-balance-calculation">
                          {t('laVacBalCal')}
                        </FormLabel>
                        <RadioGroup
                          row
                          aria-labelledby="vacation-balance-calculation"
                          name="vac-bal-calc-radio-buttons-group"
                          onChange={(event) => {
                            const value = parseInt(event.target.value);
                            if (!isNaN(value)) {
                              onChange(value.toString());
                            }
                          }}
                        >
                          {vacationCalculationDayOptions.map((option) => {
                            const optionId = `idVacBalCal${option}`;
                            return (
                              <FormControlLabel
                                key={`balance-calculation-option-${option}`}
                                id={optionId}
                                value={option}
                                control={<Radio />}
                                label={option}
                                checked={value === option}
                              />
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                    );
                  }}
                />
                <PersonnelGroupComponents control={control} />
                <Controller
                  name={'rehire.employmentDetails.fEmplDetailCalcHoursBalance'}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      label={t('laCalcHoursBalance')}
                      sx={sxStyles.inputBottomMargin}
                      control={
                        <Checkbox
                          checked={value}
                          onChange={(e) => {
                            onChange(e.target.checked);
                          }}
                        />
                      }
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={'rehire.employment.fEmplInfo'}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value}
                      multiline
                      rows={2}
                      sx={{
                        ...sxStyles.selectStyle,
                        ...sxStyles.inputBottomMargin,
                      }}
                      onChange={onChange}
                      label={t('fEmplInfo')}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <Divider />
        <DialogActions>
          {hasFormErrors && (
            <Typography color={'error'} sx={sxStyles.formErrorMessage}>
              {t('formValidationFailed')}
            </Typography>
          )}

          <Button
            type={'submit'}
            onClick={onSave()}
            disabled={submitting || hasFormErrors}
            startIcon={
              submitting ? (
                <CircularProgress size={20} />
              ) : (
                <LegacyFAIcon FAIcon={LegacyFAIcons.Save} />
              )
            }
          >
            {t('fRehireBtnLabel')}
          </Button>
          <Button
            onClick={dialogOnClose}
            startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Close} />}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </DraggableDialog>
    </>
  );
};
